import Badge from '@material-ui/core/Badge';
import { makeStyles } from '@material-ui/core/styles';
import { Box, TabBox, Tabs } from 'components/general-styles';
import { CHATS, REVIEW_LIST_TEAM_CHAT_AS_DEFAULT } from 'config';
import { DIRECT_ROOM, TEAM_ROOM } from 'globalConstants';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import AuthService from 'services/auth-service';
import PusherService from 'services/pusher-service';
import { PatientContext } from 'state/contexts/patient';
import { UserContext } from 'state/contexts/user';
import Theme from 'styles/theme';
import { playSoundMessageReceived, setFavicon } from 'utils/helpers';
import Tab from '../../../elements/tab';
import Room, { RoomName } from './room/Room';
import { ChatSection, StickyBox } from './style';

type ChatTab = {
  name: string;
  label: string;
};

type ChatRoom = {
  name: RoomName;
};

type RoomRef = {
  handleMessageReceived: (data: {
    sender_uuid: string;
    patient_uuid: string;
    room_name: string;
  }) => void;
};

type Props = {
  reviewList?: string | null;
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    '& .MuiBadge-colorSecondary': {
      backgroundColor: Theme.salmon,
    },
    '& .MuiBadge-anchorOriginTopRightRectangle': {
      top: 10,
      right: 10,
    },
  },
}));

const Chat: FC<Props> = ({ reviewList = null }) => {
  const [selected, setSelected] = useState<RoomName>(
    reviewList && REVIEW_LIST_TEAM_CHAT_AS_DEFAULT.includes(reviewList) ? TEAM_ROOM : DIRECT_ROOM
  );
  const [newDirectMsg, setNewDirectMsg] = useState<boolean>(false);
  const [newTeamMsg, setNewTeamMsg] = useState<boolean>(false);

  const roomComponentRef = useRef<RoomRef>(null);

  const { userState } = useContext(UserContext);
  const { patientState } = useContext(PatientContext);
  const patient = patientState.current!;

  const classes = useStyles();

  setFavicon(newDirectMsg || newTeamMsg);

  const onClickTab = (tab: string) => {
    if (tab === DIRECT_ROOM) {
      setNewDirectMsg(false);
    } else {
      setNewTeamMsg(false);
    }
    setSelected(tab as RoomName);
  };

  useEffect(() => {
    if (!patient) {
      return;
    }

    // The profile has no auth_token on a Hub-issued session: use the stored one.
    const pusherService = new PusherService(AuthService.getAuth()?.Authorization.token);

    // Messages between patient and provider
    const directChannel = pusherService.subscribeAndBind(
      `${CHATS.pusher[DIRECT_ROOM].channel}-${userState.uuid}`,
      {
        callback: ({ sender_uuid, patient_uuid }) => {
          if (sender_uuid === patient.uuid) {
            playSoundMessageReceived();
            setNewDirectMsg(true);

            roomComponentRef.current?.handleMessageReceived({
              sender_uuid,
              patient_uuid,
              room_name: DIRECT_ROOM,
            });
          }
        },
        eventName: CHATS.pusher[DIRECT_ROOM].event,
      }
    );

    // Messages between patient and care team
    const teamChannel = pusherService.subscribeAndBind(
      `${CHATS.pusher[TEAM_ROOM].channel}-${userState.uuid}`,
      {
        callback: ({ sender_uuid, patient_uuid }) => {
          if (sender_uuid !== userState.uuid && patient_uuid === patient.uuid) {
            playSoundMessageReceived();
            setNewTeamMsg(true);

            roomComponentRef.current?.handleMessageReceived({
              sender_uuid,
              patient_uuid,
              room_name: TEAM_ROOM,
            });
          }
        },
        eventName: CHATS.pusher[TEAM_ROOM].event,
      }
    );

    return () => {
      pusherService.disconnect([directChannel, teamChannel]);
    };
  }, [patient]);

  return (
    <ChatSection>
      <StickyBox>
        <Box>
          <Tabs>
            {CHATS.tabs.map((tab: ChatTab) => (
              <Badge
                className={classes.root}
                key={`badge-${patient.id}-${tab.name}`}
                color='secondary'
                variant='dot'
                invisible={
                  !(
                    (newDirectMsg && tab.name === DIRECT_ROOM) ||
                    (newTeamMsg && tab.name === TEAM_ROOM)
                  )
                }>
                <Tab
                  onClick={onClickTab}
                  selected={selected === tab.name}
                  tab={tab}
                  key={`tab-${patient.id}-${tab.name}`}
                />
              </Badge>
            ))}
          </Tabs>
          <TabBox>
            {(CHATS.rooms as ChatRoom[]).map(
              (room: ChatRoom) =>
                selected === room.name && (
                  <Room
                    ref={roomComponentRef}
                    patientId={patient.id}
                    room={room}
                    key={`room-${patient.id}-${room.name}`}
                    setNewDirectMsg={setNewDirectMsg}
                    setNewTeamMsg={setNewTeamMsg}
                  />
                )
            )}
          </TabBox>
        </Box>
      </StickyBox>
    </ChatSection>
  );
};

export default Chat;
